'use client';

import { useContext } from 'react';

import { FeatureFlagsContext } from '../components/feature-flags-provider';
import { DEFAULT_FEATURE_FLAGS } from '../lib/defaults';
import type { FeatureFlagKey, FeatureFlagsConfig } from '../types';

/**
 * Hook to access all feature flags from context
 * Falls back to defaults if provider not available
 *
 * Usage:
 * ```tsx
 * const flags = useFeatureFlags();
 * if (flags.enableThemeToggle) {
 *   // Show theme toggle
 * }
 * ```
 */
export function useFeatureFlags(): FeatureFlagsConfig {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    // Fallback to defaults if provider not available
    return DEFAULT_FEATURE_FLAGS;
  }

  return context;
}

/**
 * Hook to get a single feature flag value
 *
 * Usage:
 * ```tsx
 * const enableTheme = useFeatureFlag('enableThemeToggle');
 * ```
 */
export function useFeatureFlag<K extends FeatureFlagKey>(
  key: K,
): FeatureFlagsConfig[K] {
  const flags = useFeatureFlags();
  return flags[key];
}
